import React, {
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { animationIcons } from '../../utils/imagePath';
import * as Three from 'three';
import {
  applyAnimationsToObject,
  applyEasing,
  applyMoveEffect,
  applyAdditiveAnimationEffect,
  applyAnimationEffect,
  applyStaticAnimations,
  isEffectLooping,
} from '../layout/gridpage/AppLeftSection/utils/animationFunctions';
import {
  captureSubtreeMaterialOpacity,
  restoreSubtreeMaterialOpacity,
} from '../../utils/ThreeJsUtils/materialOpacitySnapshot';
import {
  resolveAnimationSceneObject,
} from './AnimationComponent/handlers/animationEffectHandlers';
import { areTimelineEffectListsEqual, snapTimeToMilli } from './utils/timelineUtils';
import { setUserDataField } from '../../utils/ThreeJsUtils/syncObjectTransform';
import {
  getTimelinePlaybackRate,
  getTimelinePlaybackTime,
  resetTimelinePlaybackClock,
  seekTimelinePlayback,
  setTimelinePlaybackRate,
} from './utils/timelinePlaybackClock';

/** Restore materials after timeline scrub: prefer per-material snapshot for groups (clouds, foliage). */
function restoreObjectOpacityFromCapture(obj, initial) {
  if (!obj || !initial) return;
  if (
    initial.materialOpacitySnapshot &&
    Object.keys(initial.materialOpacitySnapshot).length > 0
  ) {
    restoreSubtreeMaterialOpacity(obj, initial.materialOpacitySnapshot, {
      includeAppearance: false,
    });
    return;
  }
  obj.traverse((child) => {
    if (child.material) {
      if (Array.isArray(child.material)) {
        child.material.forEach((material) => {
          material.opacity =
            typeof initial.opacity === 'number' ? initial.opacity : 1;
          material.needsUpdate = true;
        });
      } else {
        child.material.opacity =
          typeof initial.opacity === 'number' ? initial.opacity : 1;
        child.material.needsUpdate = true;
      }
    }
  });
}

const AnimationTimelinePanel = ({
  // Timeline state
  timelineEffects,
  selectedEffects,
  isDraggingOverTimeline,
  setIsDraggingOverTimeline,
  window_type,
  draggedTimelineEffect,
  draggedEffect,

  // Timeline scaling and bounds
  timelineScale,
  effectStartTime,
  setEffectStartTime,
  effectEndTime,
  setEffectEndTime,

  // Current time indicator
  currentTimeIndicator,
  updateCurrentTimeIndicator,
  isDraggingIndicator,
  setIsDraggingIndicator,

  // Animation settings
  animationSettings,

  // Playback state
  isPlaying,
  setIsPlaying,
  setPlaybackStartTime,
  roundToPrecision,
  parseFloatWithPrecision,
  findNextAvailableRow,

  // Event handlers
  handleTimelineDrop,
  handleTimelineEffectDrop,
  handleTimelineEffectDragStart,
  handleTimelineEffectDragEnd,
  handleTimelineDragLeave,
  handleTimeMarkerClick,
  handleEffectResize,
  removeTimelineEffect,
  handleEffectSelect,
  clearSelection,
  handlePlayPause,
  handleGoToStart,
  handleGoToEnd,
  handleZoomSliderChange,
  updateSetting,

  // Custom components
  CustomTimelineSlider,
  renderTimeIndicator,
  completeImageObject,
  selectedObject,
}) => {
  const { animationObjects } = useSelector((state) => state.animation);
  const initialTransformsRef = useRef(new Map());
  const scene =
    completeImageObject && completeImageObject.scene
      ? completeImageObject.scene
      : null;
  const timelineRef = useRef(null);
  const timelineContainerRef = useRef(null);
  const playbackStartTimeRef = useRef(0);
  const playbackUIIntervalRef = useRef(null);
  // Track active animations for cleanup
  const activeAnimationsRef = useRef(new Map());

  // when paused, we remember how far we got so resume can continue
  const pausedTimeRef = useRef(0);

  // Refs for direct DOM manipulation and performance
  const indicatorRef = useRef(null);
  const timeTextRef = useRef(null);
  const indicatorScrollRef = useRef(null);
  const playbackRafRef = useRef(null);
  const currentPlaybackTimeRef = useRef(0);
  const playbackGenerationRef = useRef(0);
  /** Optional rate applied on the next play start (FF / reverse buttons). */
  const playbackRateOverrideRef = useRef(null);

  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);
  const [dragPreviewPosition, setDragPreviewPosition] = useState(null);


  // const activeAnimationObjects = useMemo(
  //   () => animationObjects.filter((obj) => {
  //     // Always show all objects in design and preview modes so their timeline effects are visible/playable
  //     return window_type === 'design' || window_type === 'preview' || obj.isActive !== false;
  //   }),
  //   [animationObjects, window_type]
  // );
  const activeAnimationObjects = useMemo(
    () => animationObjects.filter((obj) => {
      // For animation playback, only include objects that are active (isActive !== false)
      // regardless of window_type. In design/preview, we show all objects for visibility,
      // but only active ones should have their animations played.
      return obj.isActive !== false;
    }),
    [animationObjects]
  );


  const activeObjectIds = useMemo(() => {
    const ids = new Set();
    activeAnimationObjects.forEach((obj) => {
      if (obj.id) ids.add(obj.id);
      if (obj.name) ids.add(obj.name);
      if (obj.temp_file_name) ids.add(obj.temp_file_name);
      if (obj._id) ids.add(obj._id);
    });
    return ids;
  }, [activeAnimationObjects]);

  // Helper to detect TransformControls and its gizmo / helpers
  const isTransformControlsHelper = useCallback((obj) => {
    let current = obj;
    while (current) {
      if (
        current.isTransformControls ||
        current.isTransformControlsGizmo ||
        current.isTransformControlsPlane
      ) {
        return true;
      }
      current = current.parent;
    }
    return false;
  }, []);

  // // Stop all GLB/playModel animations for an object and its descendants (mixer can live on a child)
  // const stopGlbAnimationsForObject = useCallback((rootObj) => {
  //   if (!rootObj) return;
  //   rootObj.traverse((node) => {
  //     // Reset SkinnedMeshes to their original bind pose
  //     if (node.isSkinnedMesh) {
  //       node.pose();
  //     }

  //     if (
  //       node.userData &&
  //       node.userData.mixer &&
  //       Array.isArray(node.userData.animationActions)
  //     ) {
  //       const mixer = node.userData.mixer;

  //       // Force evaluate the first action at time 0 to reset non-skinned animated nodes
  //       if (node.userData.animationActions.length > 0) {
  //         const action = node.userData.animationActions[0];
  //         const wasPlaying = action.isRunning();
  //         action.reset().play();
  //         mixer.setTime(0);
  //       }

  //       node.userData.animationActions.forEach((action) => {
  //         action.stop();
  //         action.reset();
  //       });
  //       // Reset the clip index tracker when stopping animations
  //       node.userData.previousClipActionIndex = -1;
  //     }
  //   });
  //   if (rootObj.userData && Object.isExtensible(rootObj.userData)) {
  //     rootObj.userData.isGlbPlaying = false;
  //     rootObj.userData.activePlayModelEffect = null;
  //     // Reset the clip index tracker
  //     rootObj.userData.previousClipActionIndex = -1;
  //   }
  // }, []);



  // Replace your current stopGlbAnimationsForObject with this improved version:
  const stopGlbAnimationsForObject = useCallback((rootObj, resetToStart = true) => {
    if (!rootObj) return;

    rootObj.traverse((node) => {
      if (
        node.userData &&
        node.userData.mixer &&
        Array.isArray(node.userData.animationActions)
      ) {
        const mixer = node.userData.mixer;
        const actions = node.userData.animationActions;

        actions.forEach((action) => {
          if (resetToStart) {
            action.stop();
            action.reset();
          } else {
            action.paused = true;   // Just pause, preserve time
          }
        });

        if (resetToStart) {
          mixer.setTime(0);
          mixer.update(0);
          node.updateMatrixWorld(true);
        }

        node.userData.previousClipActionIndex = -1;
      }
    });

    setUserDataField(rootObj, 'isGlbPlaying', false);
    setUserDataField(rootObj, 'activePlayModelEffect', null);
    setUserDataField(rootObj, 'previousClipActionIndex', -1);
  }, []);

  // Reset function to clear internal state
  const resetInternalState = () => {
    setCurrentPlaybackTime(0);
    setDragPreviewPosition(null);

    // Clear any running playback intervals
    if (playbackRafRef.current) {
      cancelAnimationFrame(playbackRafRef.current);
      playbackRafRef.current = null;
    }
    if (playbackUIIntervalRef.current) {
      clearInterval(playbackUIIntervalRef.current);
      playbackUIIntervalRef.current = null;
    }

    // Stop per-object animation loops so preview/design switches don't fight transforms
    activeAnimationsRef.current.forEach((animationIdRef) => {
      if (animationIdRef?.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
    });
    activeAnimationsRef.current.clear();
    playbackGenerationRef.current += 1;

    // Reset timeline bounds to default
    setEffectStartTime(0);
    setEffectEndTime(7);

    // Reset all objects to their original state before clearing captured transforms
    resetAllObjects();
    initialTransformsRef.current.clear();

    // clear paused time
    pausedTimeRef.current = 0;
    window.__timelinePlaybackPaused = false;
    window.__timelinePlaybackActive = false;
  };

  // Listen for reset animation state event
  useEffect(() => {
    const handleResetAnimationState = () => {
      resetInternalState();
    };

    const handleStopTimelinePlayback = () => {
      if (playbackRafRef.current) {
        cancelAnimationFrame(playbackRafRef.current);
        playbackRafRef.current = null;
      }
      if (playbackUIIntervalRef.current) {
        clearInterval(playbackUIIntervalRef.current);
        playbackUIIntervalRef.current = null;
      }
      activeAnimationsRef.current.forEach((animationIdRef) => {
        if (animationIdRef?.current) {
          cancelAnimationFrame(animationIdRef.current);
          animationIdRef.current = null;
        }
      });
      activeAnimationsRef.current.clear();
      playbackGenerationRef.current += 1;
      setIsPlaying?.(false);
      setPlaybackStartTime?.(null);
      pausedTimeRef.current = 0;
      currentPlaybackTimeRef.current = 0;
      setCurrentPlaybackTime(0);
      window.__timelinePlaybackPaused = false;
      window.__timelinePlaybackActive = false;
    };

    window.addEventListener('resetAnimationState', handleResetAnimationState);
    window.addEventListener('stopTimelinePlayback', handleStopTimelinePlayback);

    return () => {
      window.removeEventListener(
        'resetAnimationState',
        handleResetAnimationState
      );
      window.removeEventListener(
        'stopTimelinePlayback',
        handleStopTimelinePlayback
      );
    };
  }, [setIsPlaying, setPlaybackStartTime]);

  // Timeline calculations
  const PIXELS_PER_MARKER = 100;

  // Timeline duration calculation
  const TIMELINE_DURATION = useMemo(() => {
    if (!timelineScale || timelineScale <= 0) {
      return 100;
    }

    const baseDuration = 100;
    const scaledDuration = baseDuration * Math.max(1, timelineScale / 2);

    let maxTime = 0;
    if (timelineEffects && timelineEffects.length > 0) {
      maxTime = Math.max(
        ...timelineEffects.map((effect) => effect.endTime || 0)
      );
    }

    // Also consider object durations
    if (animationObjects && animationObjects.length > 0) {
      const maxObjDuration = Math.max(
        ...animationObjects.map((obj) => Number(obj.duration || 0))
      );
      maxTime = Math.max(maxTime, maxObjDuration);
    }

    const calculatedDuration = Math.max(scaledDuration, maxTime + 20);
    return Math.max(100, calculatedDuration);
  }, [timelineScale, timelineEffects, animationObjects]);

  // Timeline width calculation
  const timelineWidth = useMemo(() => {
    if (!timelineScale || timelineScale <= 0) return 1000;

    const markerInterval = timelineScale;
    const totalMarkers = Math.ceil(TIMELINE_DURATION / markerInterval);
    const calculatedWidth = totalMarkers * PIXELS_PER_MARKER;

    return Math.max(1000, calculatedWidth);
  }, [TIMELINE_DURATION, timelineScale]);

  // Convert time to pixel position
  const timeToPixels = useCallback(
    (time) => {
      if (!timelineScale || timelineScale <= 0) return 0;
      const markerIndex = time / timelineScale;
      return markerIndex * PIXELS_PER_MARKER;
    },
    [timelineScale]
  );

  // Convert pixels to time
  const pixelsToTime = useCallback(
    (pixels) => {
      if (!timelineScale || timelineScale <= 0) return 0;
      const markerIndex = pixels / PIXELS_PER_MARKER;
      return markerIndex * timelineScale;
    },
    [timelineScale]
  );

  // Generate time markers
  const timeMarkers = useMemo(() => {
    if (!timelineScale || timelineScale <= 0) {
      return [];
    }

    const markers = [];
    const markerInterval = timelineScale;
    const maxMarkers = Math.ceil(TIMELINE_DURATION / markerInterval);

    // Explicitly add 0s marker as the first element
    markers.push({
      time: 0,
      position: 0, // Ensure position starts at 0 pixels
      interval: markerInterval,
      showLine: true, // Force line to show at 0s
    });

    for (let multiplier = 1; multiplier <= maxMarkers; multiplier++) {
      const time = multiplier * markerInterval;
      if (time > TIMELINE_DURATION) break;

      const position = timeToPixels(time);

      markers.push({
        time: Number(time.toFixed(3)),
        position: position,
        interval: markerInterval,
        showLine: true
      });
    }

    return markers;
  }, [timelineScale, timeToPixels, TIMELINE_DURATION]);
  // Helper to update the left panel timer DOM element directly
  const updateLeftPanelTimer = useCallback((time) => {
    if (window.animationLeftPanelTimer) {
      const mm = Math.floor(time / 60).toString().padStart(2, '0');
      const ss = Math.floor(time % 60).toString().padStart(2, '0');
      const cs = Math.floor((time % 1) * 100).toString().padStart(2, '0');
      window.animationLeftPanelTimer.innerText = `${mm}:${ss}:${cs}`;
    }
  }, []);

  const resolveCapturedTransform = useCallback((objectId, obj) => {
    const map = initialTransformsRef.current;
    return (
      map.get(objectId) ||
      (obj?.name ? map.get(obj.name) : undefined) ||
      (obj?.temp_file_name ? map.get(obj.temp_file_name) : undefined)
    );
  }, []);

  /** Scale/move/rotate animations multiply from this baseline — must stay at pre-play rest pose. */
  const syncAnimationBaselinesFromCapture = useCallback(
    (obj, objectId) => {
      if (!obj) return;

      const initial = resolveCapturedTransform(objectId, obj);
      if (!obj.userData) obj.userData = {};
      if (!Object.isExtensible(obj.userData)) {
        obj.userData = { ...obj.userData };
      }

      if (initial) {
        obj.userData.originalPosition = initial.position.clone();
        obj.userData.originalRotation = initial.rotation.clone();
        obj.userData.originalScale = initial.scale.clone();
        obj.userData.framePosition = initial.position.clone();
        obj.userData.frameRotation = initial.rotation.clone();
        obj.userData.frameScale = initial.scale.clone();
      } else {
        obj.userData.originalPosition = obj.position.clone();
        obj.userData.originalRotation = obj.rotation.clone();
        obj.userData.originalScale = obj.scale.clone();
        obj.userData.framePosition = obj.position.clone();
        obj.userData.frameRotation = obj.rotation.clone();
        obj.userData.frameScale = obj.scale.clone();
      }
    },
    [resolveCapturedTransform]
  );

  // Scrubbing helper to apply animations at a specific time
  const scrubAllObjectsToTime = useCallback((time) => {
    if (!scene || !timelineEffects) return;

    // Update captured transforms to ensure we use current scale data
    // ONLY if we are at time 0 and not playing - this prevents dirtying original transforms
    if (time === 0) {
      updateCapturedTransforms();
    }

    // Group effects by object
    const effectsByObject = timelineEffects.reduce((acc, effect) => {
      if (!activeObjectIds.has(effect.objectId)) return acc;
      if (!acc[effect.objectId]) acc[effect.objectId] = [];
      acc[effect.objectId].push(effect);
      return acc;
    }, {});

    Object.keys(effectsByObject).forEach((objectId) => {
      const obj = resolveAnimationSceneObject(scene, objectId);
      if (obj) {
        syncAnimationBaselinesFromCapture(obj, objectId);
        applyStaticAnimations(obj, effectsByObject[objectId], time);
      }
    });
  }, [
    scene,
    timelineEffects,
    activeObjectIds,
    syncAnimationBaselinesFromCapture,
  ]);

  // Handle timeline click
  const handleTimelineClick = useCallback(
    (e) => {
      if (!timelineRef.current || !timelineContainerRef.current) return;

      // Don't handle clicks on timeline effects or time indicator
      if (
        e.target.closest('.timeline-effect') ||
        e.target.closest('[style*="left:"]')
      ) {
        return;
      }

      const containerRect =
        timelineContainerRef.current.getBoundingClientRect();
      const scrollLeft = timelineContainerRef.current.scrollLeft;
      const relativeClickX = e.clientX - containerRect.left;
      const absoluteClickX = relativeClickX + scrollLeft;
      const clampedX = Math.max(0, Math.min(timelineWidth, absoluteClickX));

      const markerIndex = clampedX / PIXELS_PER_MARKER;
      const clickedTime = markerIndex * timelineScale;
      const clampedTime = Math.max(0, Math.min(TIMELINE_DURATION, clickedTime));
      const percentage = (clampedTime / TIMELINE_DURATION) * 100;

      setCurrentPlaybackTime(clampedTime);
      currentPlaybackTimeRef.current = clampedTime;
      if (!isPlaying) {
        pausedTimeRef.current = clampedTime;
        window.__timelinePlaybackPaused = clampedTime > 0.01;
        // Immediate preview on click
        scrubAllObjectsToTime(clampedTime);
      }

      // Direct DOM update so the label reflects the clicked position instantly
      if (indicatorRef.current) {
        indicatorRef.current.style.left = `${percentage}%`;
      }
      if (timeTextRef.current) {
        timeTextRef.current.innerText = `${clampedTime.toFixed(2)}s`;
      }

      // Sync left panel timer
      updateLeftPanelTimer(clampedTime);

      // Update indicator with the exact time
      updateCurrentTimeIndicator?.(percentage, clampedTime, true);
      // Don't call handleTimeMarkerClick as it might override with different values
    },
    [
      timelineScale,
      timelineWidth,
      TIMELINE_DURATION,
      updateCurrentTimeIndicator,
      isPlaying,
      scrubAllObjectsToTime,
    ]
  );

  // Handle indicator dragging
  const handleIndicatorMouseMove = useCallback(
    (e) => {
      if (
        !isDraggingIndicator ||
        !timelineRef.current ||
        !timelineContainerRef.current
      )
        return;

      const containerRect =
        timelineContainerRef.current.getBoundingClientRect();
      const scrollLeft = timelineContainerRef.current.scrollLeft;
      const relativeMouseX = e.clientX - containerRect.left;
      const absoluteMouseX = relativeMouseX + scrollLeft;
      const clampedX = Math.max(0, Math.min(timelineWidth, absoluteMouseX));

      const time = pixelsToTime(clampedX);
      const clampedTime = Math.max(0, Math.min(TIMELINE_DURATION, time));
      const percentage = (clampedTime / TIMELINE_DURATION) * 100;

      setCurrentPlaybackTime(clampedTime);
      currentPlaybackTimeRef.current = clampedTime;
      if (!isPlaying) {
        pausedTimeRef.current = clampedTime;
        window.__timelinePlaybackPaused = clampedTime > 0.01;
        // Apply real-time scrubbing animations if not playing
        scrubAllObjectsToTime(clampedTime);
      }

      // Direct DOM updates for smooth 60fps response (same as playback RAF loop)
      if (indicatorRef.current) {
        indicatorRef.current.style.left = `${percentage}%`;
      }
      if (timeTextRef.current) {
        timeTextRef.current.innerText = `${clampedTime.toFixed(2)}s`;
      }

      // Sync left panel timer
      updateLeftPanelTimer(clampedTime);

      updateCurrentTimeIndicator?.(percentage, clampedTime, true);
    },
    [
      isDraggingIndicator,
      pixelsToTime,
      timelineWidth,
      TIMELINE_DURATION,
      updateCurrentTimeIndicator,
      isPlaying,
      scrubAllObjectsToTime,
    ]
  );

  const handleIndicatorMouseUp = useCallback(() => {
    setIsDraggingIndicator(false);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, [setIsDraggingIndicator]);

  // Calculate max effect end time
  const getMaxEffectEndTime = useCallback(() => {
    let maxTime = 0;

    // 1. Calculate max time from timeline effects first (the primary source of truth for the indicator)
    if (timelineEffects && timelineEffects.length > 0) {
      const activeEffects = timelineEffects.filter((effect) =>
        activeObjectIds.has(effect.objectId)
      );
      if (activeEffects.length > 0) {
        maxTime = Math.max(
          ...activeEffects.map((effect) => effect.endTime || 0)
        );
      }
    }

    // 2. Only if no effects were found, fallback to object durations (e.g. for GLB animations without explicit effects)
    if (
      maxTime === 0 &&
      activeAnimationObjects &&
      activeAnimationObjects.length > 0
    ) {
      const maxObjDuration = Math.max(
        ...activeAnimationObjects.map((obj) => Number(obj.duration || 0))
      );
      maxTime = Math.max(maxTime, maxObjDuration);
    }

    return maxTime;
  }, [timelineEffects, activeAnimationObjects, activeObjectIds]);

  const hasActiveLoopingEffects = useCallback(() => {
    return (timelineEffects || []).some(
      (effect) =>
        activeObjectIds.has(effect.objectId) && isEffectLooping(effect)
    );
  }, [timelineEffects, activeObjectIds]);

  /** Cap playhead display at the last clip only when nothing is looping. */
  const clampPlayheadDisplayTime = useCallback(
    (elapsed) => {
      const t = Math.max(0, Number(elapsed) || 0);
      if (hasActiveLoopingEffects()) return t;
      const playEndTime = getMaxEffectEndTime();
      if (playEndTime > 0 && t > playEndTime) return playEndTime;
      return t;
    },
    [getMaxEffectEndTime, hasActiveLoopingEffects]
  );

  const getCurrentTime = useCallback(() => {
    return clampPlayheadDisplayTime(
      currentTimeIndicator?.time ?? currentPlaybackTime
    );
  }, [currentTimeIndicator, currentPlaybackTime, clampPlayheadDisplayTime]);

  const getCurrentPercentage = useCallback(() => {
    const displayTime = clampPlayheadDisplayTime(currentPlaybackTime);
    if (!TIMELINE_DURATION) return 0;
    return (displayTime / TIMELINE_DURATION) * 100;
  }, [currentPlaybackTime, TIMELINE_DURATION, clampPlayheadDisplayTime]);

  const cancelAllObjectAnimationLoops = useCallback(() => {
    activeAnimationsRef.current.forEach((animationIdRef) => {
      if (animationIdRef?.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
    });
    activeAnimationsRef.current.clear();
  }, []);

  // Playback controls
  // Stop playback helper
  /**
   * Stop playback completely and reset to start.
   * Used when user presses pause and wants to restart from beginning or when
   * going to start/end.
   */
  const stopPlayback = useCallback(
    (resetToStart = true) => {
      if (playbackRafRef.current) {
        cancelAnimationFrame(playbackRafRef.current);
        playbackRafRef.current = null;
      }
      if (playbackUIIntervalRef.current) {
        clearInterval(playbackUIIntervalRef.current);
        playbackUIIntervalRef.current = null;
      }

      // Stop all active animations (these are per-object requestAnimationFrame loops)
      cancelAllObjectAnimationLoops();
      if (!resetToStart) {
        // pause path: generation bump invalidates any stale RAF callbacks
        playbackGenerationRef.current += 1;
      }

      // Handle GLB/playModel animations
      if (scene) {
        const objectIds = new Set([
          ...initialTransformsRef.current.keys(),
          ...(timelineEffects || []).map((e) => e.objectId),
        ]);
        objectIds.forEach((objectId) => {
          const obj = resolveAnimationSceneObject(scene, objectId);
          if (obj && !isTransformControlsHelper(obj)) {
            stopGlbAnimationsForObject(obj, resetToStart);
          }
        });
      }

      setIsPlaying?.(false);
      setPlaybackStartTime?.(null);
      window.__timelinePlaybackActive = false;
      window.__timelinePlaybackPaused = !resetToStart;

      if (resetToStart) {
        pausedTimeRef.current = 0;
        currentPlaybackTimeRef.current = 0;
      }
    },
    [
      setIsPlaying,
      setPlaybackStartTime,
      stopGlbAnimationsForObject,
      scene,
      timelineEffects,
      cancelAllObjectAnimationLoops,
    ]
  );

  // Playback controls
  const handlePlayPauseClick = useCallback(() => {
    if (isPlaying) {
      // Pause at the live clock. Only treat "past last clip" as finished
      // when there are no looping effects (loops keep time advancing).
      const timeAtPause = Math.max(0, getTimelinePlaybackTime());
      const playEndTime = getMaxEffectEndTime();
      const hasLoopingEffects = hasActiveLoopingEffects();
      const completed =
        !hasLoopingEffects &&
        playEndTime > 0 &&
        timeAtPause >= playEndTime - 0.01;
      const displayTime = clampPlayheadDisplayTime(timeAtPause);
      currentPlaybackTimeRef.current = completed ? 0 : timeAtPause;
      pausedTimeRef.current = completed ? 0 : timeAtPause;
      setCurrentPlaybackTime(displayTime);
      scrubAllObjectsToTime(completed ? displayTime : timeAtPause);
      stopPlayback(false); // do not reset objects
      resetTimelinePlaybackClock(completed ? 0 : timeAtPause, 1);

      const percentage = TIMELINE_DURATION
        ? (displayTime / TIMELINE_DURATION) * 100
        : 0;
      if (indicatorRef.current) {
        indicatorRef.current.style.left = `${percentage}%`;
      }
      if (timeTextRef.current) {
        timeTextRef.current.innerText = `${displayTime.toFixed(2)}s`;
      }
      updateLeftPanelTimer(displayTime);
      updateCurrentTimeIndicator?.(percentage, displayTime, true);
    } else {
      // playing or resuming
      if (!timelineEffects || timelineEffects.length === 0) return;

      const maxEndTime = getMaxEffectEndTime();
      const playEndTime = maxEndTime > 0 ? maxEndTime : 0;

      setIsPlaying(true);
      window.__timelinePlaybackPaused = false;
      window.__timelinePlaybackActive = true;
      playbackGenerationRef.current += 1;

      cancelAllObjectAnimationLoops();

      if (
        pausedTimeRef.current > 0 &&
        pausedTimeRef.current < playEndTime - 0.01
      ) {
        // resume from paused time
        const resumeAt = pausedTimeRef.current;
        const startRate = playbackRateOverrideRef.current ?? 1;
        playbackRateOverrideRef.current = null;
        resetTimelinePlaybackClock(resumeAt, startRate);
        const startMs = Date.now() - resumeAt * 1000;
        setPlaybackStartTime?.(startMs);
        playbackStartTimeRef.current = startMs;

        // restore indicator and playback state
        const resumeDisplay = clampPlayheadDisplayTime(resumeAt);
        setCurrentPlaybackTime(resumeDisplay);
        currentPlaybackTimeRef.current = resumeAt;
        const pct = TIMELINE_DURATION
          ? (resumeDisplay / TIMELINE_DURATION) * 100
          : 0;
        updateCurrentTimeIndicator?.(pct, resumeDisplay, true);
        if (indicatorRef.current) {
          indicatorRef.current.style.left = `${pct}%`;
        }
        if (timeTextRef.current) {
          timeTextRef.current.innerText = `${resumeDisplay.toFixed(2)}s`;
        }
        updateLeftPanelTimer(resumeDisplay);

        // Update captured transforms ONLY if starting from 0
        if (resumeAt === 0) {
          updateCapturedTransforms();
        }

        // restart animations from offset
        startAllAnimationsLocal(resumeAt);
        pausedTimeRef.current = 0;
      } else {
        // Fresh start from the beginning. Only capture rest pose when the
        // playhead is at 0 — otherwise (e.g. after Go to End) objects are in an
        // animated pose and capturing would overwrite the saved start state.
        const atStart =
          (currentPlaybackTimeRef.current || 0) <= 0.01 &&
          (pausedTimeRef.current || 0) <= 0.01;

        if (atStart) {
          captureInitialTransforms();
          updateCapturedTransforms();
        }
        resetAllObjects(); // Restore saved rest pose before starting

        const startRate = playbackRateOverrideRef.current ?? 1;
        playbackRateOverrideRef.current = null;
        // Reverse from a paused mid-point should resume there, not jump to 0
        const startAt =
          startRate < 0 && currentPlaybackTimeRef.current > 0
            ? currentPlaybackTimeRef.current
            : 0;
        resetTimelinePlaybackClock(startAt, startRate);
        const startMs = Date.now() - startAt * 1000;
        setPlaybackStartTime?.(startMs);
        playbackStartTimeRef.current = startMs;

        // Reset timeline indicator to start (or resume for reverse)
        setCurrentPlaybackTime(startAt);
        currentPlaybackTimeRef.current = startAt;
        updateCurrentTimeIndicator?.(
          (startAt / TIMELINE_DURATION) * 100,
          startAt,
          true
        );

        startAllAnimationsLocal(startAt);
        pausedTimeRef.current = 0;
      }

      // Start optimized playback loop using requestAnimationFrame
      const updatePlayback = () => {
        if (!window.__timelinePlaybackActive) return;

        let elapsed = getTimelinePlaybackTime();
        const rate = getTimelinePlaybackRate();

        // Reverse hit the start
        if (rate < 0 && elapsed <= 0) {
          elapsed = 0;
          currentPlaybackTimeRef.current = 0;
          pausedTimeRef.current = 0;
          setCurrentPlaybackTime(0);
          scrubAllObjectsToTime(0);
          stopPlayback(false);
          resetTimelinePlaybackClock(0, 1);
          if (indicatorRef.current) indicatorRef.current.style.left = '0%';
          if (timeTextRef.current) timeTextRef.current.innerText = '0.00s';
          updateLeftPanelTimer(0);
          updateCurrentTimeIndicator?.(0, 0, true);
          return;
        }

        // Forward past the last effect:
        // - no loops → stop and reset
        // - any loop → keep playing and keep the indicator moving with elapsed time
        const hasLoopingEffects = hasActiveLoopingEffects();
        const atLastEffect = rate > 0 && playEndTime > 0 && elapsed >= playEndTime;

        if (atLastEffect && !hasLoopingEffects) {
          stopPlayback(true);
          resetAllObjects();
          resetTimelinePlaybackClock(0, 1);
          setCurrentPlaybackTime(0);
          currentPlaybackTimeRef.current = 0;
          pausedTimeRef.current = 0;
          updateCurrentTimeIndicator?.(0, 0, true);

          // Directly update the indicator DOM element to 0 position when animation completes
          if (indicatorRef.current) {
            indicatorRef.current.style.left = '0%';
          }
          if (timeTextRef.current) {
            timeTextRef.current.innerText = '0.00s';
          }
          // Reset left panel timer
          updateLeftPanelTimer(0);
        } else {
          elapsed = Math.max(0, elapsed);
          currentPlaybackTimeRef.current = elapsed;

          const indicatorTime = elapsed;
          const percentage = Math.min(
            100,
            (indicatorTime / TIMELINE_DURATION) * 100
          );

          if (indicatorRef.current) {
            indicatorRef.current.style.left = `${percentage}%`;
          }
          if (timeTextRef.current) {
            timeTextRef.current.innerText = `${indicatorTime.toFixed(2)}s`;
          }

          updateLeftPanelTimer(indicatorTime);

          playbackRafRef.current = requestAnimationFrame(updatePlayback);
        }
      };

      playbackRafRef.current = requestAnimationFrame(updatePlayback);
    }
  }, [
    isPlaying,
    stopPlayback,
    scrubAllObjectsToTime,
    getMaxEffectEndTime,
    TIMELINE_DURATION,
    setIsPlaying,
    setPlaybackStartTime,
    updateCurrentTimeIndicator,
    scene,
    timelineEffects,
    currentPlaybackTime,
    activeObjectIds,
    cancelAllObjectAnimationLoops,
    clampPlayheadDisplayTime,
    hasActiveLoopingEffects,
    updateLeftPanelTimer,
  ]);

  /**
   * Begin per-object animation loops.  An optional startOffset (seconds)
   * can be provided to simulate having already run for some time – used when
   * resuming from a pause.
   */
  const startAllAnimationsLocal = (startOffset = 0) => {
    cancelAllObjectAnimationLoops();

    // Group effects by object
    const effectsByObject = timelineEffects.reduce((acc, effect) => {
      if (!activeObjectIds.has(effect.objectId)) return acc;
      if (!acc[effect.objectId]) {
        acc[effect.objectId] = [];
      }
      acc[effect.objectId].push(effect);
      return acc;
    }, {});

    // Apply animations for each object
    Object.keys(effectsByObject).forEach((objectId) => {
      const obj = resolveAnimationSceneObject(scene, objectId);

      if (obj) {
        // Always animate from the captured pre-play rest pose (e.g. scale 1,1,1),
        // not the mid-animation scale left over from a previous pause.
        syncAnimationBaselinesFromCapture(obj, objectId);

        const animations = effectsByObject[objectId];
        const animationIdRef = { current: null };
        applyAnimationsToObject(
          obj,
          animations,
          animationIdRef,
          startOffset,
          playbackGenerationRef
        );
        activeAnimationsRef.current.set(objectId, animationIdRef);
      }
    });
  };

  // Helper functions

  const resetInactiveObjects = (affectedObjects, newTime, timelineEffects) => {
    initialTransformsRef.current.forEach((initial, key) => {
      if (!affectedObjects.has(key)) {
        const obj = resolveAnimationSceneObject(scene, key);
        // Skip transform controls helpers
        if (!obj || isTransformControlsHelper(obj)) return;

        // Check if object has any active effects (including looping ones)
        const hasActiveEffect = timelineEffects.some((effect) => {
          // Try both key and effect.objectId matches
          if (effect.objectId !== key) return false;

          if (effect.loop === true) {
            // For looping effects, check if we're past the start time
            return newTime >= effect.startTime;
          } else {
            // For non-looping effects, use normal time range check
            return newTime >= effect.startTime && newTime <= effect.endTime;
          }
        });

        if (!hasActiveEffect) {
          obj.position.copy(initial.position);
          obj.rotation.copy(initial.rotation);
          obj.scale.copy(initial.scale);

          restoreObjectOpacityFromCapture(obj, initial);

          // Pause GLB animations if playModel effect is inactive (check subtree; mixer may be on child)
          const hasActivePlayModel = timelineEffects.some((effect) => {
            if (
              effect.objectId !== key ||
              effect.effectType !== 'playModel'
            )
              return false;
            if (effect.loop === true) {
              return newTime >= effect.startTime;
            }
            return newTime >= effect.startTime && newTime <= effect.endTime;
          });
          if (!hasActivePlayModel) {
            stopGlbAnimationsForObject(obj);
          }
        }
      }
    });
  };

  const resetAllObjects = () => {
    if (!scene) return;

    // Reset all objects that have initial transforms captured
    initialTransformsRef.current.forEach((initial, key) => {
      const obj = resolveAnimationSceneObject(scene, key);
      // Skip transform controls helpers
      if (!obj || isTransformControlsHelper(obj)) return;
      {
        obj.position.copy(initial.position);
        obj.rotation.copy(initial.rotation);
        obj.scale.copy(initial.scale);

        restoreObjectOpacityFromCapture(obj, initial);

        // Stop all GLB animations (mixer may be on a child, so traverse subtree)
        stopGlbAnimationsForObject(obj);
      }
    });

    // Also reset any objects that might not be in the initial transforms map
    // This ensures all objects return to their default state
    if (timelineEffects) {
      timelineEffects.forEach((effect) => {
        const obj = resolveAnimationSceneObject(scene, effect.objectId);
        if (obj && !initialTransformsRef.current.has(effect.objectId)) {
          // Reset to default values if no initial transform was captured
          obj.position.set(0, 0, 0);
          obj.rotation.set(0, 0, 0);
          obj.scale.set(1, 1, 1);

          // Particle groups (clouds, foliage, etc.): do not force subtree opacity to 1
          if (obj.deleteType !== 'particle') {
            obj.traverse((child) => {
              if (child.material) {
                if (Array.isArray(child.material)) {
                  child.material.forEach((material) => {
                    material.opacity = 1;
                    material.needsUpdate = true;
                  });
                } else {
                  child.material.opacity = 1;
                  child.material.needsUpdate = true;
                }
              }
            });
          }
        }

        // Stop GLB animations for objects with playModel effects (mixer may be on child)
        if (obj) stopGlbAnimationsForObject(obj);
      });
    }
  };

  const captureInitialTransforms = () => {
    const map = new Map();
    if (!scene) return;

    // Capture transforms for all relevant objects in the scene, not just those with effects
    scene.traverse((child) => {
      // Ignore TransformControls gizmo / helpers completely
      if (isTransformControlsHelper(child)) return;

      if (child.isMesh || child.isGroup) {
        // Capture objects that have names (not unnamed helper objects)
        if (child.name && child.name !== '') {
          const transformData = {
            position: child.position.clone(),
            rotation: child.rotation.clone(),
            scale: child.scale.clone(),
            opacity: child.material ? (child.material.opacity ?? 1) : 1,
            materialOpacitySnapshot: captureSubtreeMaterialOpacity(child),
          };
          // Store by both name and temp_file_name to ensure lookups work
          map.set(child.name, transformData);
          if (child.temp_file_name && child.temp_file_name !== child.name) {
            map.set(child.temp_file_name, transformData);
          }
        }
      }
    });

    // Also capture transforms for objects that have effects (in case they're not in the scene yet)
    if (timelineEffects) {
      timelineEffects.forEach((effect) => {
        const obj = resolveAnimationSceneObject(scene, effect.objectId);

        if (obj && !map.has(effect.objectId)) {
          const transformData = {
            position: obj.position.clone(),
            rotation: obj.rotation.clone(),
            scale: obj.scale.clone(),
            opacity: obj.material ? (obj.material.opacity ?? 1) : 1,
            materialOpacitySnapshot: captureSubtreeMaterialOpacity(obj),
          };
          map.set(effect.objectId, transformData);
          // Also store by object name if different
          if (obj.name && obj.name !== effect.objectId) {
            map.set(obj.name, transformData);
          }
          // Also store by temp_file_name if it exists
          if (obj.temp_file_name && obj.temp_file_name !== effect.objectId && obj.temp_file_name !== obj.name) {
            map.set(obj.temp_file_name, transformData);
          }
        }
      });
    }

    initialTransformsRef.current = map;
  };

  // Update captured transforms with current scene state (call right before animation)
  const updateCapturedTransforms = () => {
    if (!scene) return;

    // Update scale and position for all captured objects with their current values
    initialTransformsRef.current.forEach((transformData, key) => {
      const obj = resolveAnimationSceneObject(scene, key);

      if (obj) {
        // Update with current values - this ensures we always use the actual current state
        transformData.position = obj.position.clone();
        transformData.rotation = obj.rotation.clone();
        transformData.scale = obj.scale.clone();
        transformData.opacity = obj.material ? (obj.material.opacity ?? 1) : 1;
        transformData.materialOpacitySnapshot =
          captureSubtreeMaterialOpacity(obj);
      }
    });
  };

  const handleGoToStartClick = useCallback(() => {
    stopPlayback(true);
    // Reset objects and move indicator back to start
    resetAllObjects();
    setCurrentPlaybackTime(0);
    updateCurrentTimeIndicator?.(0, 0, true);
    pausedTimeRef.current = 0;

    // Directly update the indicator DOM element to 0 position
    if (indicatorRef.current) {
      indicatorRef.current.style.left = '0%';
    }
    if (timeTextRef.current) {
      timeTextRef.current.innerText = '0.00s';
    }

    // Reset left panel timer
    updateLeftPanelTimer(0);

    // Scroll timeline container to show the start
    requestAnimationFrame(() => {
      if (timelineContainerRef.current) {
        timelineContainerRef.current.scrollLeft = 0;

        // Sync header scroll
        if (window.timelineHeaderScroll) {
          window.timelineHeaderScroll.scrollLeft = 0;
        }
      }
    });

    handleGoToStart?.();
  }, [stopPlayback, updateCurrentTimeIndicator, handleGoToStart]);

  const handleGoToEndClick = useCallback(() => {
    stopPlayback(false); // Stop playback but don't reset to start

    const maxEndTime = getMaxEffectEndTime();
    const seekTime =
      maxEndTime > 0 ? maxEndTime : TIMELINE_DURATION > 0 ? TIMELINE_DURATION : 0;

    // Ensure a rest-pose capture exists before scrubbing to the end pose.
    // Never re-capture while objects may already be mid-animation.
    if (
      initialTransformsRef.current.size === 0 &&
      (currentPlaybackTimeRef.current || 0) <= 0.01 &&
      (pausedTimeRef.current || 0) <= 0.01
    ) {
      captureInitialTransforms();
    }

    scrubAllObjectsToTime(seekTime);

    // Update timeline indicator and internal state
    setCurrentPlaybackTime(seekTime);
    currentPlaybackTimeRef.current = seekTime;
    const percentage = (seekTime / TIMELINE_DURATION) * 100;
    updateCurrentTimeIndicator?.(percentage, seekTime, true);
    pausedTimeRef.current = seekTime;

    // Directly update the indicator DOM element and time text
    if (indicatorRef.current) {
      indicatorRef.current.style.left = `${percentage}%`;
    }
    if (timeTextRef.current) {
      timeTextRef.current.innerText = `${seekTime.toFixed(2)}s`;
    }

    // Update left panel timer display
    updateLeftPanelTimer(seekTime);

    // Scroll timeline container to show the indicator at the end of the timeline
    requestAnimationFrame(() => {
      if (timelineContainerRef.current && timelineRef.current) {
        const indicatorPixelPosition = timeToPixels(seekTime);
        const containerWidth = timelineContainerRef.current.clientWidth;
        const centerOffset = containerWidth / 2;
        const scrollPosition = Math.max(0, indicatorPixelPosition - centerOffset);

        timelineContainerRef.current.scrollLeft = scrollPosition;

        // Sync header scroll
        if (window.timelineHeaderScroll) {
          window.timelineHeaderScroll.scrollLeft = scrollPosition;
        }
      }
    });

    handleGoToEnd?.();
  }, [
    stopPlayback,
    getMaxEffectEndTime,
    TIMELINE_DURATION,
    updateCurrentTimeIndicator,
    handleGoToEnd,
    scrubAllObjectsToTime,
    timeToPixels,
  ]);

  // Event listeners for indicator dragging
  useEffect(() => {
    if (isDraggingIndicator) {
      document.addEventListener('mousemove', handleIndicatorMouseMove);
      document.addEventListener('mouseup', handleIndicatorMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleIndicatorMouseMove);
        document.removeEventListener('mouseup', handleIndicatorMouseUp);
      };
    }
  }, [isDraggingIndicator, handleIndicatorMouseMove, handleIndicatorMouseUp]);

  // Capture initial transforms when component mounts
  useEffect(() => {
    if (scene) {
      captureInitialTransforms();
    }
  }, [scene]);

  // When an object is deleted (animationObjects shrinks) or timeline effects change,
  // stop playback, clear the timeline indicator interval, and reset the indicator to 0.
  // Also reset all objects to their initial state so they don't get stuck in a transformed state.
  const prevTimelineEffectsRef = useRef(timelineEffects);
  const prevAnimationObjectIdsRef = useRef('');

  useEffect(() => {
    const effectsChanged = !areTimelineEffectListsEqual(
      prevTimelineEffectsRef.current,
      timelineEffects
    );
    const animationObjectIds = (animationObjects || [])
      .map((obj) => obj.id)
      .sort()
      .join('|');
    const objectsChanged =
      prevAnimationObjectIdsRef.current !== animationObjectIds;

    if (effectsChanged || objectsChanged) {
      prevTimelineEffectsRef.current = timelineEffects;
      prevAnimationObjectIdsRef.current = animationObjectIds;

      // Always reset to 0 if there are no timeline effects
      const hasNoEffects = !timelineEffects || timelineEffects.length === 0;

      if (hasNoEffects || isPlaying || currentPlaybackTime > 0) {
        stopPlayback(true);
        resetAllObjects();
        setIsPlaying?.(false);
        setCurrentPlaybackTime(0);
        currentPlaybackTimeRef.current = 0;
        updateCurrentTimeIndicator?.(0, 0, true);
        pausedTimeRef.current = 0;

        // Directly update the indicator DOM element to 0 position
        if (indicatorRef.current) {
          indicatorRef.current.style.left = '0%';
        }
        if (timeTextRef.current) {
          timeTextRef.current.innerText = '0.00s';
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationObjects, timelineEffects]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playbackUIIntervalRef.current) {
        clearInterval(playbackUIIntervalRef.current);
        playbackUIIntervalRef.current = null;
      }
    };
  }, []);

  // Stop playback when isPlaying changes externally
  useEffect(() => {
    if (!isPlaying && playbackUIIntervalRef.current) {
      clearInterval(playbackUIIntervalRef.current);
      playbackUIIntervalRef.current = null;
    }
  }, [isPlaying]);

  return (
    <div className="flex-1 overflow-hidden bg-[#131418]">
      {/* Top Control Bar */}
      <div className="h-10 bg-[#131418] border-b border-[#4D4D4D] flex items-center px-4 relative z-40">
        <div className="flex items-center space-x-4">
          {selectedEffects.length > 0 ? (
            // Show selected effect controls
            <>
              <div className="flex items-center space-x-2">
                <span className="text-[10px] text-[#FFFFFF] font-medium">
                  Start:
                </span>
                <input
                  type="number"
                  value={Math.min(
                    roundToPrecision(animationSettings?.startTime || 0, 3),
                    120
                  )}
                  max={120}
                  onChange={(e) => {
                    let newStartTime = Math.max(
                      0,
                      parseFloatWithPrecision(e.target.value, 3)
                    );
                    newStartTime = Math.min(newStartTime, 120);
                    // Update the selected effect's start time using updateSetting
                    if (selectedEffects.length === 1 && updateSetting) {
                      updateSetting('startTime', newStartTime);
                    }
                  }}
                  className="h-5 w-16 bg-[#2B2C2F] text-[10px] text-gray-200 px-2 rounded focus:outline-none focus:border-[#EE2B2A] transition-colors"
                  step="0.001"
                  min="0"
                />
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-[10px] text-[#FFFFFF] font-medium">
                  End:
                </span>
                <input
                  type="number"
                  value={(() => {
                    if (selectedEffects.length === 1) {
                      const effect = timelineEffects?.find(
                        (e) => e.id === selectedEffects[0]
                      );
                      const end = effect
                        ? effect.endTime
                        : (animationSettings?.startTime || 0) +
                          (animationSettings?.duration || 1);
                      return roundToPrecision(end, 3);
                    }
                    return roundToPrecision(
                      (animationSettings?.startTime || 0) +
                        (animationSettings?.duration || 1),
                      3
                    );
                  })()}
                  onChange={(e) => {
                    let newEndTime = Math.max(
                      (animationSettings?.startTime || 0) + 0.001,
                      parseFloatWithPrecision(e.target.value, 3)
                    );
                    newEndTime = Math.min(newEndTime, 120);
                    // Update the selected effect's end time
                    if (selectedEffects.length === 1) {
                      const effectId = selectedEffects[0];
                      const effect = timelineEffects?.find(
                        (item) => item.id === effectId
                      );
                      if (handleEffectResize) {
                        handleEffectResize(
                          effectId,
                          newEndTime,
                          effect
                            ? {
                                duration: Math.max(
                                  0.001,
                                  (Number(effect.endTime) || 0) -
                                    (Number(effect.startTime) || 0)
                                ),
                                animationSpeed:
                                  effect.animationSpeed !== undefined
                                    ? effect.animationSpeed
                                    : 1,
                                playSpeed:
                                  effect.playSpeed !== undefined
                                    ? effect.playSpeed
                                    : 1,
                              }
                            : null
                        );
                      }
                    }
                  }}
                  className="h-5 w-16 bg-[#2B2C2F] text-[10px] text-gray-200 px-2 rounded focus:outline-none focus:border-[#EE2B2A] transition-colors"
                  step="0.001"
                  min={(animationSettings?.startTime || 0) + 0.001}
                />
              </div>
            </>
          ) : (
            // Show timeline view controls
            <>
              <div className="flex items-center space-x-1">
                <span className="text-[10px] text-[#ffffff] font-medium">
                  View Start:
                </span>
                <input
                  type="number"
                  value={Math.min(
                    roundToPrecision(animationSettings?.startTime || 0, 1),
                    120
                  )}
                  max={120}
                  onChange={(e) => {
                    let newStartTime = Math.max(
                      0,
                      parseFloatWithPrecision(e.target.value, 1)
                    );
                    newStartTime = Math.min(newStartTime, 120);
                    if (setEffectStartTime) setEffectStartTime(value);
                  }}
                  className="h-5 w-12 bg-[rgba(51,53,57,0.6)] text-[10px] text-gray-200 px-2 rounded-[5px] focus:outline-none focus:border-red-500 transition-colors"
                  step="0.1"
                />
              </div>
              <div className="flex items-center space-x-1">
                <span className="text-[10px] text-[#ffffff] font-medium">
                  View End:
                </span>
                <input
                  type="number"
                  value={Math.min(
                    roundToPrecision(effectEndTime || 10, 1),
                    120
                  )}
                  max={120}
                  onChange={(e) => {
                    const value = parseFloatWithPrecision(e.target.value, 1);
                    if (setEffectEndTime) setEffectEndTime(value);
                  }}
                  className="h-5 w-12 bg-[#2B2C2F] text-[10px] text-gray-200 px-2 rounded-[5px] focus:outline-none focus:border-red-500 transition-colors"
                  step="0.1"
                />
              </div>
            </>
          )}
        </div>

        <div className="ml-auto flex items-center space-x-2">
          {/* Play/Pause Controls */}
          <button
            onClick={handleGoToStartClick}
            className="h-5 w-10 bg-[#2B2C2F] rounded-[5px] hover:bg-[#EE2B2A] transition-colors flex items-center justify-center"
            title="Go to start"
          >
            <img
              className="w-3 h-3"
              src={animationIcons?.Ani8}
              alt="Go to start"
            />
          </button>
          <button
            onClick={handlePlayPauseClick}
            className="h-5 w-10 bg-[#2B2C2F] rounded-[5px] hover:bg-[#EE2B2A] transition-colors flex items-center justify-center"
            title={isPlaying ? 'Pause animation' : 'Play animation'}
          >
            <img
              className="w-3 h-3"
              src={isPlaying ? animationIcons?.Ani17 : animationIcons?.Ani9}
              alt={isPlaying ? 'Pause' : 'Play'}
            />
          </button>
          <button
            onClick={handleGoToEndClick}
            className="h-5 w-10 bg-[#2B2C2F] rounded-[5px] hover:bg-[#EE2B2A] transition-colors flex items-center justify-center"
            title="Go to end"
          >
            <img
              className="w-3 h-3"
              src={animationIcons?.Ani10}
              alt="Go to end"
            />
          </button>
          <div className="w-[1px] h-[40px] bg-[#454545]"></div>
          <div className="flex items-center space-x-2">
            <span className="text-[10px] text-white font-medium">
              {roundToPrecision(timelineScale || 1, 1)}s
            </span>
            <CustomTimelineSlider
              value={timelineScale || 1}
              onChange={handleZoomSliderChange}
              min={0.1}
              max={10}
              step={0.1}
            />
          </div>
        </div>
      </div>

      {/* Timeline Container */}
      <div className="w-full h-[82%] flex flex-col overflow-hidden relative">
        {/* Fixed Time Markers Header Wrapper */}
        <div className="h-8 bg-[#131418]  border-b-4 border-[rgba(255,131,130,0.1)] overflow-hidden relative z-30">
          <div
            className="timeline-header-scroll overflow-x-auto overflow-y-hidden h-full"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
            ref={(el) => {
              if (el) {
                // Store header scroll element for syncing
                window.timelineHeaderScroll = el;
              }
            }}
          >
            <style>{`
      .timeline-header-scroll::-webkit-scrollbar {
        display: none;
      }
    `}</style>
            <div
              ref={timelineRef}
              className="relative h-full cursor-pointer"
              style={{
                width: `${timelineWidth}px`,
                minWidth: `${timelineWidth}px`,
              }}
              data-timeline="true"
              onClick={handleTimelineClick}
            >
              {timeMarkers.map((marker, index) => {
                let decimalPlaces = 0;
                if (marker.interval <= 0.01) {
                  decimalPlaces = 3;
                } else if (marker.interval <= 0.1) {
                  decimalPlaces = 2;
                } else if (marker.interval < 1) {
                  decimalPlaces = 1;
                } else if (marker.interval % 1 === 0) {
                  decimalPlaces = 0;
                } else {
                  decimalPlaces = 1;
                }

                return (
                  <div
                    key={`${marker.time}-${index}`}
                    className="absolute bottom-0 pointer-events-none"
                    style={{
                      left: `${marker.position}px`,
                      transform: 'translateX(-50%)',
                      ...(marker.time === 0 && { transform: 'translateX(0)' }),
                    }}
                  >
                    <div className="text-[10px] text-[#B8B8B8] whitespace-nowrap font-mono text-center mb-1">
                      {marker.time.toFixed(decimalPlaces)}s
                    </div>
                    {marker.showLine && (
                      <div className="w-px h-full bg-[#454545] absolute left-1/2 transform -translate-x-1/2"></div>
                    )}
                  </div>
                );
              })}

              {/* Minor tick marks */}
              {timeMarkers.map((marker, index) => {
                const nextMarker = timeMarkers[index + 1];
                if (!nextMarker) return null;

                const timeDiff = nextMarker.time - marker.time;
                const pixelDiff = nextMarker.position - marker.position;

                if (pixelDiff < 60) return null;

                const minorTicks = [];

                for (let i = 1; i < 5; i++) {
                  const minorTime = marker.time + (timeDiff * i) / 5;
                  const minorPosition = timeToPixels(minorTime);

                  minorTicks.push(
                    <div
                      key={`minor-${marker.time}-${i}`}
                      className="absolute bottom-0 pointer-events-none text-[#B8B8B8]"
                      style={{
                        left: `${minorPosition}px`,
                        transform: 'translateX(-50%)',
                      }}
                    >
                      <div className="w-px h-2 bg-[#454545] opacity-50 relative left-1"></div>
                    </div>
                  );
                }

                return minorTicks;
              })}
            </div>
          </div>
        </div>

        {/* Scrollable Content Area with Layers */}
        <div className="flex-1 relative overflow-hidden bg-[#131418]">

          {/* Layer 2: Main Content (Animation Objects - Scrollable) */}
          <div
            ref={(el) => {
              timelineContainerRef.current = el;
              if (el) {
                window.animationTimelineScroll = el;
              }
            }}
            className="absolute inset-0 overflow-auto animation-scrollbar"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#444447 #1a1b1e',
            }}
            onScroll={(e) => {
              if (window.timelineHeaderScroll) {
                window.timelineHeaderScroll.scrollLeft = e.target.scrollLeft;
              }
              if (indicatorScrollRef.current) {
                indicatorScrollRef.current.scrollLeft = e.target.scrollLeft;
              }
              if (window.animationLeftPanelScroll) {
                window.animationLeftPanelScroll.scrollTop = e.target.scrollTop;
              }
            }}
          >
            <div
              className="relative bg-transparent"
              style={{
                minWidth: `${timelineWidth}px`,
                width: `${timelineWidth}px`,
                minHeight: '100%',
              }}
            >
              <div className="relative pt-2">
                {animationObjects && animationObjects.length > 0 ? (
                  animationObjects.map((obj) => {
                    const isInactive = obj.isActive === false && window_type !== 'preview';
                    const objectEffects =
                      timelineEffects?.filter(
                        (effect) => effect.objectId === obj.id
                      ) || [];
                    const usedRows = [
                      ...new Set(
                        objectEffects.map((effect) => effect.row || 0)
                      ),
                    ].sort((a, b) => a - b);
                    // Calculate row count based on number of unique rows (not maximum row index)
                    // This ensures height adjusts when rows are deleted
                    const rowCount = usedRows.length > 0 ? usedRows.length : 1;
                    const objectHeight = Math.max(40, rowCount * 28 + 12);
                    return (
                      <div
                        key={obj.id}
                        className="relative flex items-start mt-1"
                        style={{
                          height: `${objectHeight}px`,
                          minHeight: '40px',
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();

                          // Check if dragging an effect from a different object
                          if (
                            draggedTimelineEffect &&
                            draggedTimelineEffect.objectId !== obj.id
                          ) {
                            // Show "not allowed" cursor
                            e.dataTransfer.dropEffect = 'none';
                            setIsDraggingOverTimeline(false);
                            return;
                          }

                          if (setIsDraggingOverTimeline)
                            setIsDraggingOverTimeline(true);
                          e.dataTransfer.dropEffect = draggedTimelineEffect
                            ? 'move'
                            : 'copy';

                          if (draggedTimelineEffect) {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            const dropX = e.clientX - rect.left;
                            const timelineContainer =
                              e.currentTarget.closest('.overflow-auto');
                            const scrollLeft = timelineContainer
                              ? timelineContainer.scrollLeft
                              : 0;
                            const absoluteX = dropX + scrollLeft;
                            const PIXELS_PER_MARKER = 100;
                            const markerIndex = absoluteX / PIXELS_PER_MARKER;
                            const timePosition = markerIndex * timelineScale;
                            const effectDuration =
                              draggedTimelineEffect.endTime -
                              draggedTimelineEffect.startTime;
                            const startPixels = timeToPixels(timePosition);
                            const endPixels = timeToPixels(
                              timePosition + effectDuration
                            );
                            const width = Math.max(20, endPixels - startPixels);

                            setDragPreviewPosition({
                              left: startPixels,
                              width: width,
                              time: timePosition,
                            });
                          }
                        }}
                        onDragLeave={(e) => {
                          handleTimelineDragLeave(e);
                          setDragPreviewPosition(null);
                        }}
                        onDrop={(e) => {
                          try {
                            const data = JSON.parse(
                              e.dataTransfer.getData('application/json')
                            );

                            // If it's a timeline effect being moved
                            if (data.type === 'timelineEffect') {
                              // Check if the effect belongs to this object
                              if (
                                draggedTimelineEffect &&
                                draggedTimelineEffect.objectId === obj.id
                              ) {
                                handleTimelineEffectDrop(e, obj.id);
                              } else {
                              }
                            } else {
                              // New effect from sidebar - allow drop
                              handleTimelineDrop(e, obj.id);
                            }
                          } catch (error) {
                            handleTimelineDrop(e, obj.id);
                          }
                          setDragPreviewPosition(null);
                        }}
                        onClick={(e) => {
                          const clickedOnEffect =
                            e.target.closest('.timeline-effect');
                          if (clickedOnEffect) {
                            return;
                          }
                          e.stopPropagation();
                          if (handleEffectSelect) {
                            const objectInfo = {
                              objectId: obj.id,
                              id: null,
                            };
                            handleEffectSelect(objectInfo, obj, e);
                          }
                        }}
                      >
                        <div
                          style={{ width: `${timelineWidth}px` }}
                          className={`absolute inset-0 transition-colors duration-150 ${isInactive
                            ? 'bg-[#1B1C20] opacity-60'
                            : selectedObject === obj.id
                              ? 'bg-[#2B2C2F]'
                              : 'bg-transparent'
                            }`}
                        >
                          <div
                            className="absolute left-0 top-2 w-36 px-2 py-1 bg-[#2B2C2F] rounded text-[10px] text-gray-300 font-medium truncate z-10"
                            style={{ display: 'none' }}
                          >
                            {obj.name || `Object ${obj.id}`}
                          </div>

                          <div
                            className="relative w-full h-full "
                            style={{ width: `${timelineWidth}px` }}
                          >
                            {objectEffects.map((effect) => {
                              const startPixels = timeToPixels(
                                effect.startTime
                              );
                              const endPixels = timeToPixels(effect.endTime);
                              const width = Math.max(
                                20,
                                endPixels - startPixels
                              );
                              const isSelected = selectedEffects?.includes(
                                effect.id
                              );
                              // Use the index in the sorted usedRows array for positioning
                              const effectRowIndex = usedRows.indexOf(
                                effect.row || 0
                              );
                              const rowOffset =
                                effectRowIndex >= 0 ? effectRowIndex * 28 : 0;

                              return (
                                <div
                                  key={effect.id}
                                  className={`timeline-effect absolute h-6 rounded-md flex items-center justify-between px-2 transition-all duration-200 group ${isInactive
                                    ? 'bg-[#7A2A2A] border border-gray-700 opacity-50 cursor-not-allowed'
                                    : isSelected
                                      ? 'bg-[#EE2B2E] shadow-lg border-2 border-white cursor-move'
                                      : 'bg-[#EE2B2E] border border-gray-600 hover:border-gray-400 cursor-move'
                                    } ${draggedTimelineEffect?.id === effect.id ? 'opacity-50' : ''}`}
                                  style={{
                                    left: `${startPixels}px`,
                                    width: `${width}px`,
                                    top: `${8 + rowOffset}px`,
                                    zIndex: isSelected ? 25 : 15,
                                  }}
                                  draggable={!isInactive}
                                  onDragStart={(e) => {
                                    if (isInactive) return;
                                    e.stopPropagation();
                                    if (handleTimelineEffectDragStart) {
                                      handleTimelineEffectDragStart(e, effect);
                                    }
                                  }}
                                  onDragEnd={(e) => {
                                    e.stopPropagation();
                                    if (handleTimelineEffectDragEnd) {
                                      handleTimelineEffectDragEnd(e);
                                    }
                                  }}
                                  onClick={(e) => {
                                    if (isInactive) return;
                                    e.stopPropagation();
                                    if (handleEffectSelect) {
                                      handleEffectSelect(effect, obj, e);
                                    }
                                  }}
                                  title={`${effect.name} - ${effect.startTime.toFixed(1)}s to ${effect.endTime.toFixed(1)}s - Drag to reposition`}
                                >
                                  <img
                                    className="w-3 h-3 opacity-90 pointer-events-none flex-shrink-0"
                                    src={effect.icon}
                                    alt={effect.name}
                                  />
                                  <span className="text-[9px] text-white font-medium truncate flex-1 mx-1 pointer-events-none">
                                    {effect.name}
                                  </span>
                                  <span
                                    className={`text-[14px] text-white transition-opacity z-10 flex-shrink-0 ${isInactive
                                      ? 'opacity-30 cursor-not-allowed'
                                      : 'opacity-0 group-hover:opacity-100 cursor-pointer hover:text-red-300'
                                      }`}
                                    onClick={(e) => {
                                      if (isInactive) return;
                                      e.stopPropagation();
                                      e.preventDefault();
                                      if (removeTimelineEffect)
                                        removeTimelineEffect(effect.id);
                                    }}
                                  >
                                    ×
                                  </span>

                                  <div
                                    className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize bg-transparent hover:bg-white hover:bg-opacity-20 z-10"
                                    onMouseDown={(e) => {
                                      if (isInactive) return;
                                      e.stopPropagation();
                                      e.preventDefault();
                                      const startX = e.clientX;
                                      const startEndTime = snapTimeToMilli(
                                        effect.endTime
                                      );
                                      const resizeOrigin = {
                                        duration: Math.max(
                                          0.001,
                                          snapTimeToMilli(
                                            (Number(effect.endTime) || 0) -
                                              (Number(effect.startTime) || 0)
                                          )
                                        ),
                                        animationSpeed:
                                          effect.animationSpeed !== undefined
                                            ? effect.animationSpeed
                                            : 1,
                                        playSpeed:
                                          effect.playSpeed !== undefined
                                            ? effect.playSpeed
                                            : 1,
                                      };

                                      const handleMouseMove = (moveEvent) => {
                                        const deltaX =
                                          moveEvent.clientX - startX;
                                        const deltaTime =
                                          pixelsToTime(deltaX) -
                                          pixelsToTime(0);
                                        const newEndTime = Math.max(
                                          snapTimeToMilli(
                                            effect.startTime + 0.001
                                          ),
                                          snapTimeToMilli(
                                            startEndTime + deltaTime
                                          )
                                        );
                                        if (handleEffectResize)
                                          handleEffectResize(
                                            effect.id,
                                            newEndTime,
                                            resizeOrigin
                                          );
                                      };

                                      const handleMouseUp = () => {
                                        document.removeEventListener(
                                          'mousemove',
                                          handleMouseMove
                                        );
                                        document.removeEventListener(
                                          'mouseup',
                                          handleMouseUp
                                        );
                                      };

                                      document.addEventListener(
                                        'mousemove',
                                        handleMouseMove
                                      );
                                      document.addEventListener(
                                        'mouseup',
                                        handleMouseUp
                                      );
                                    }}
                                  />
                                </div>
                              );
                            })}

                            {draggedEffect && isDraggingOverTimeline && (
                              <div className="absolute inset-0   pointer-events-none bg-[#ffffff1e]   z-5 rounded-md"></div>
                            )}

                            {draggedTimelineEffect &&
                              isDraggingOverTimeline && (
                                <div className="absolute inset-0  pointer-events-none  z-5 rounded-md"></div>
                              )}

                            {dragPreviewPosition &&
                              draggedTimelineEffect &&
                              isDraggingOverTimeline && (
                                <div
                                  className="absolute h-6 rounded-md  pointer-events-none z-30 flex items-center justify-center"
                                  style={{
                                    left: `${dragPreviewPosition.left}px`,
                                    width: `${dragPreviewPosition.width}px`,
                                    top: '4px',
                                  }}
                                >
                                  {/* <span className="text-[9px] text-white font-medium">
                                    {draggedTimelineEffect.name}
                                  </span> */}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    className="flex items-center justify-center h-full bg-[#131418]"
                    style={{ width: `${timelineWidth}px`, minHeight: '100px' }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (setIsDraggingOverTimeline) setIsDraggingOverTimeline(true);
                    }}
                    onDragLeave={() => {
                      if (setIsDraggingOverTimeline) setIsDraggingOverTimeline(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (setIsDraggingOverTimeline) setIsDraggingOverTimeline(false);
                      try {
                        const data = JSON.parse(e.dataTransfer.getData('application/json'));
                        if (data.type === 'animationEffect') {
                          toast.error('Please add an animation object to the list before adding effects', {
                            style: {
                              background: '#1d1e21',
                              color: '#fff',
                            },
                          });
                        }
                      } catch (error) {
                        console.error('Error parsing drop data:', error);
                      }
                    }}
                  />
                )}
              </div>

              {/* Vertical Lines (Moved after rows to ensure they show above backgrounds) */}
              <div
                className="absolute inset-0 pointer-events-none z-5 overflow-hidden"
                style={{ width: `${timelineWidth}px`, height: '100%' }}
              >
                {timeMarkers.map((marker, index) => {
                  if (!marker.showLine) return null;

                  return (
                    <div
                      key={`vertical-${marker.time}-${index}`}
                      className="absolute top-0 pointer-events-none"
                      style={{
                        left: `${marker.position}px`,
                        transform: 'translateX(-50%)',
                        height: '100%',
                      }}
                    >
                      <div className="w-px h-full bg-[#454545]"></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Layer 3: Foreground (Playhead Indicator - Fixed Vertically) */}
          <div
            ref={indicatorScrollRef}
            className="absolute inset-0 pointer-events-none overflow-hidden z-30"
          >
            <div
              style={{
                width: `${timelineWidth}px`,
                height: '100%',
                position: 'relative',
              }}
            >
              {renderTimeIndicator &&
                renderTimeIndicator(
                  getCurrentPercentage(),
                  getCurrentTime(),
                  true,
                  setIsDraggingIndicator,
                  isDraggingIndicator,
                  scrubAllObjectsToTime,
                  indicatorRef,
                  timeTextRef
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimationTimelinePanel;